import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";

// 3rd party library
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import jwtDecode from "jwt-decode";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Common components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { TableCell } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import FontComponent from "components/Responsive/fonts";

// Constants
import Constants, { defaultData, Colors } from "utils/Constants";
import TableLoader from "components/Table/TableLoader";
import Session from "utils/Sessions";

// Redux
import { useSelector } from "react-redux";

function ReportCalculationTable({
  table,
  isSorted,
  loading,
  licenseRequired,
  backgroundColor, // New prop to specify the background color
  textColor,
  containerHeight,
  extraContent,
  extraHeaders,
  footerList,
  variant,
}) {
  const [status, setStatus] = useState(Constants.PENDING);
  const tableContainerRef = useRef(null);
  const [autoHeight, setAutoHeight] = useState(null);
  const ConfigData = useSelector((state) => state.config);
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const hasRows = data.length > 0;
  const resolvedHeight = containerHeight || autoHeight;
  const shouldClampHeight = hasRows && Boolean(containerHeight || autoHeight);
  const computedMaxHeight = shouldClampHeight ? resolvedHeight : "none";
  const extraHeadersArray = Array.isArray(extraHeaders) ? extraHeaders : [];
  const footerListArray = Array.isArray(footerList) ? footerList : [];
  const extraHeadersCount = extraHeadersArray.length;
  const footerListCount = footerListArray.length;
  const isDrawerVariant = variant === "drawer";
  const fontSize = FontComponent({
    sizes: {
      xs: pxToRem(12),
      sm: pxToRem(13),
      md: pxToRem(13),
      lg: pxToRem(13),
      xl: pxToRem(13),
      xxl: pxToRem(14),
      fhd: pxToRem(14),
      uhd: pxToRem(15),
    },
  }); // Compact typography keeps headers readable while saving space
  const compactHeaderPadding = `${pxToRem(6)} ${pxToRem(10)}`; // Slimmer header padding aligns with new height
  const compactBodyPadding = `${pxToRem(4)} ${pxToRem(10)}`; // Reduced body padding for shorter rows
  const compactBodyFontSize = pxToRem(12); // Slightly smaller body text for dense layout
  const compactRowHeight = pxToRem(32); // Consistent compact row height across the table
  const drawerHeaderPadding = `${pxToRem(10)} ${pxToRem(14)}`;
  const drawerBodyPadding = `${pxToRem(8)} ${pxToRem(14)}`;
  const drawerRowHeight = pxToRem(40);
  const rowHeight = isDrawerVariant ? drawerRowHeight : compactRowHeight;
  const bodyPadding = isDrawerVariant ? drawerBodyPadding : compactBodyPadding;
  const headerPadding = isDrawerVariant ? drawerHeaderPadding : compactHeaderPadding;
  const cellBorderColor = isDrawerVariant ? "rgba(25, 26, 81, 0.12)" : "#E0E6F5";

  const tableInstance = useTable({ columns, data }, useGlobalFilter, useSortBy, usePagination);

  const { getTableProps, getTableBodyProps, headerGroups, rows, pageOptions } = tableInstance;

  // update the status on role change, when superadmin login as admin and data changes
  useEffect(() => {
    const token = jwtDecode(Session.userToken);
    const { role } = token;

    const isSuperAdmin =
      role === defaultData.SUPER_ADMIN_ROLE && !Session.isSuperAdminViewingAdminPanel;
    const isPending = ConfigData.loading === Constants.PENDING || loading === Constants.PENDING;
    const isRejected = ConfigData.loading === Constants.REJECTED || loading === Constants.REJECTED;
    const isFulfilledWithData =
      loading === Constants.FULFILLED && rows.length > 0 && pageOptions.length > 0;
    const isFulfilledNoData =
      loading === Constants.FULFILLED && rows.length === 0 && pageOptions.length === 0;
    const isConfigFulfilled = ConfigData.loading === "fulfilled";

    let nextStatus = status;

    // for superadmin
    if (isSuperAdmin || !licenseRequired) {
      if (loading === Constants.PENDING) nextStatus = Constants.PENDING;
      else if (isRejected) nextStatus = Constants.REJECTED;
      else if (isFulfilledWithData) nextStatus = Constants.FULFILLED;
      else if (isFulfilledNoData) nextStatus = "noData";
    }
    // for admin
    else if (isPending) nextStatus = Constants.PENDING;
    else if (isRejected) nextStatus = Constants.REJECTED;
    else if (isConfigFulfilled && isFulfilledWithData) nextStatus = Constants.FULFILLED;
    else if (isConfigFulfilled && isFulfilledNoData) nextStatus = "noData";

    setStatus((prevStatus) => (prevStatus === nextStatus ? prevStatus : nextStatus));
  }, [
    Session.userToken,
    Session.isSuperAdminViewingAdminPanel,
    ConfigData.loading,
    loading,
    rows.length,
    pageOptions.length,
    licenseRequired,
    status,
  ]);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const handleCellBackground = (value) => {
    if (!value) return "transparent";

    // Check if the value is a date
    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    if (datePattern.test(value)) {
      return Colors.LIGHT_GREEN3;
    }

    if (value) {
      const numericValue = parseFloat(value.replace("%", ""));
      if (numericValue <= 50) {
        return Colors.PINK;
      }
      if (numericValue <= 75) {
        return Colors.LIGHT_PINK;
      }
      if (numericValue < 100) {
        return Colors.LIGHT_ORANGE1;
      }
      if (numericValue === 100) {
        return Colors.LIGHT_GREEN3;
      }
      return "transparent";
    }
    return "transparent";
  };

  const HEADER_ROW_HEIGHT = isDrawerVariant ? 42 : 36;
  const headerRowHeightRem = pxToRem(HEADER_ROW_HEIGHT); // Keeps sticky offset (px) and CSS height (rem) in sync
  const HEADER_BASE_Z_INDEX = 40;
  const EXTRA_HEADER_BASE_Z_INDEX = HEADER_BASE_Z_INDEX + 10;

  const updateAutoHeight = useCallback(() => {
    if (containerHeight || typeof window === "undefined") return;

    const element = tableContainerRef.current;
    if (!element) return;

    window.requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(element);
      const marginTop = parseFloat(computedStyles.marginTop || "0");
      const paddingTop = parseFloat(computedStyles.paddingTop || "0");
      const marginBottom = parseFloat(computedStyles.marginBottom || "0");
      const paddingBottom = parseFloat(computedStyles.paddingBottom || "0");
      const borderBottomWidth = parseFloat(computedStyles.borderBottomWidth || "0");
      const topOffset = rect.top + marginTop + paddingTop;
      const bottomOffset = marginBottom + paddingBottom + borderBottomWidth;
      const availableHeight = window.innerHeight - topOffset - bottomOffset;

      if (availableHeight > 0) {
        setAutoHeight(`calc(100vh - ${Math.ceil(topOffset)}px - ${Math.ceil(bottomOffset)}px)`);
      } else {
        setAutoHeight(null);
      }
    });
  }, [containerHeight]);

  useEffect(() => {
    if (containerHeight) {
      setAutoHeight(null);
      return;
    }
    updateAutoHeight();
  }, [containerHeight, updateAutoHeight]);

  useEffect(() => {
    if (containerHeight || typeof window === "undefined") return undefined;

    const handleResize = () => {
      updateAutoHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [containerHeight, updateAutoHeight]);

  useEffect(() => {
    updateAutoHeight();
  }, [updateAutoHeight, rows.length, extraHeadersCount, footerListCount, status, isDrawerVariant]);

  return (
    <MDBox
      sx={{
        backgroundColor: Colors.WHITE,
        borderRadius: isDrawerVariant ? pxToRem(16) : "10px",
        border: `1px solid ${cellBorderColor}`,
        width: "100%",
        boxShadow: isDrawerVariant ? "0px 10px 30px rgba(25, 26, 81, 0.08)" : "none",
      }}
    >
      {/* Keep header rows opaque and layered above the scrollable content */}
      <TableContainer
        ref={tableContainerRef}
        sx={{
          boxShadow: "none",
          // Keep height fluid when there is no data to avoid large empty space.
          height: "auto",
          maxHeight: computedMaxHeight,
          overflow: "auto",
          position: "relative",
          backgroundColor: Colors.WHITE,
          zIndex: 1,
          overflowX: "auto",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": isDrawerVariant
            ? {
                height: pxToRem(8),
                width: pxToRem(8),
              }
            : undefined,
          "&::-webkit-scrollbar-track": isDrawerVariant
            ? {
                backgroundColor: Colors.LIGHT_GRAY4,
                borderRadius: pxToRem(8),
              }
            : undefined,
          "&::-webkit-scrollbar-thumb": isDrawerVariant
            ? {
                backgroundColor: "rgba(25, 26, 81, 0.35)",
                borderRadius: pxToRem(8),
              }
            : undefined,
          "&::-webkit-scrollbar-thumb:hover": isDrawerVariant
            ? {
                backgroundColor: "rgba(25, 26, 81, 0.55)",
              }
            : undefined,
        }}
      >
        <Table
          {...getTableProps()}
          stickyHeader
          sx={{
            minWidth: isDrawerVariant ? pxToRem(1100) : "auto",
          }}
        >
          <MDBox
            component="thead"
            sx={{
              position: "relative",
              zIndex: HEADER_BASE_Z_INDEX,
              backgroundColor: Colors.WHITE,
            }}
          >
            {extraHeadersArray.length > 0 &&
              extraHeadersArray?.map((headercolumns, idx) => (
                <TableRow key={`extra-header-row-${headercolumns.map((col) => col.key).join("-")}`}>
                  {headercolumns?.map((column, colIdx) => (
                    <TableCell
                      key={column.key}
                      component="th"
                      scope="col"
                      align={column.align ? column.align : "left"}
                      colSpan={column.colSpan ? column.colSpan : 1}
                      sx={({ typography: { fontWeightBold } }) => ({
                        fontSize,
                        fontWeight: fontWeightBold,
                        backgroundColor: Colors.WHITE,
                        color: Colors.PRIMARY,
                        width: column.width ? column.width : "auto",
                        border: column.Header !== "" ? `1px solid ${cellBorderColor}` : "none",
                        borderTop: "none",
                        borderBottom: idx === extraHeadersArray.length - 1 && "none",
                        borderRight: colIdx === headercolumns.length - 1 && "none",
                        position: "sticky",
                        top: `${idx * HEADER_ROW_HEIGHT}px`,
                        zIndex: EXTRA_HEADER_BASE_Z_INDEX + (extraHeadersArray.length - idx),
                        padding: headerPadding, // Apply compact padding to sticky extra headers
                        height: headerRowHeightRem,
                        minHeight: headerRowHeightRem,
                        backgroundClip: "padding-box",
                      })}
                    >
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <MDTypography
                          sx={{
                            fontSize,
                            fontWeight: "bold",
                            textTransform: "none",
                            textAlign: "center",
                          }}
                        >
                          {column.Header}
                        </MDTypography>
                      </MDBox>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {headerGroups.map((headerGroup, headerGroupIdx) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <DataTableHeadCell
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sorted={setSortedValue(column)}
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                    stickyTopOffset={`${
                      (extraHeadersArray.length + headerGroupIdx) * HEADER_ROW_HEIGHT
                    }px`}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody
            {...getTableBodyProps()}
            sx={
              isDrawerVariant
                ? {
                    "& .MuiTableRow-root": {
                      transition: "background-color 0.2s ease",
                    },
                    "& .MuiTableRow-root:nth-of-type(odd)": {
                      backgroundColor: Colors.WHITE,
                    },
                    "& .MuiTableRow-root:nth-of-type(even)": {
                      backgroundColor: Colors.BOX_BG,
                    },
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: "rgba(25, 26, 81, 0.1)",
                    },
                  }
                : {}
            }
          >
            {(() => {
              switch (status) {
                case Constants.PENDING:
                  return (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <TableLoader />
                      </TableCell>
                    </TableRow>
                  );

                case Constants.FULFILLED: {
                  const rowData = rows?.map((row) => {
                    const { original: locationObj } = row;
                    if (locationObj?.assets?.length === 0) {
                      return (
                        <TableRow key={locationObj?.key}>
                          <TableCell
                            key={locationObj?.key}
                            colSpan={locationObj?.colSpan || 1}
                            align={locationObj?.align || "left"}
                            rowSpan={locationObj?.rowSpan || 1}
                            width={locationObj?.width || "auto"}
                            sx={({ typography: { fontWeightBold } }) => ({
                              fontSize: compactBodyFontSize,
                              fontWeight: fontWeightBold,
                              color: Colors.PRIMARY,
                              backgroundColor: "transparent",
                              border: `1px solid ${cellBorderColor}`,
                              borderLeft: "none",
                              padding: bodyPadding,
                              height: "auto",
                              minHeight: rowHeight, // Keep compact height consistent across rows
                            })}
                          >
                            {locationObj.location}
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={1}
                            sx={({ typography: { fontWeightBold } }) => ({
                              fontSize: compactBodyFontSize,
                              fontWeight: fontWeightBold,
                              border: `1px solid ${cellBorderColor}`,
                              backgroundColor: "transparent",
                              color: Colors.PRIMARY,
                              padding: bodyPadding,
                              height: "auto",
                              minHeight: rowHeight,
                            })}
                          />
                          {locationObj.reports.map((report) => {
                            if (report.type === "location") {
                              return (
                                <TableCell
                                  key={report?.key}
                                  align={report?.align || "left"}
                                  rowSpan={report?.rowSpan || 1}
                                  colSpan={report?.colSpan || 1}
                                  width={report?.width || "auto"}
                                  sx={({ typography: { fontWeightBold } }) => ({
                                    fontSize: compactBodyFontSize,
                                    fontWeight: fontWeightBold,
                                    border: `1px solid ${cellBorderColor}`,
                                    backgroundColor:
                                      report?.reportList[0]?.bgColor || "transparent",
                                    color: Colors.PRIMARY,
                                    padding: bodyPadding,
                                    height: "auto",
                                    minHeight: rowHeight,
                                  })}
                                >
                                  {report.reportList[0].completion}
                                </TableCell>
                              );
                            }
                            if (
                              report.type === "asset_per_location" ||
                              report.type === "multiple_assets"
                            ) {
                              return (
                                <TableCell
                                  key={report?.key}
                                  align={report?.align || "left"}
                                  rowSpan={report?.rowSpan || 1}
                                  colSpan={report?.colSpan || 1}
                                  width={report?.width || "auto"}
                                  sx={({ typography: { fontWeightBold } }) => ({
                                    fontSize: compactBodyFontSize,
                                    fontWeight: fontWeightBold,
                                    border: `1px solid ${cellBorderColor}`,
                                    backgroundColor:
                                      report?.reportList[0]?.bgColor || "transparent",
                                    color: Colors.PRIMARY,
                                    padding: bodyPadding,
                                    height: "auto",
                                    minHeight: rowHeight,
                                  })}
                                >
                                  {report.reportList[0].completion}
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                          <TableCell
                            align={locationObj?.locationCompletion?.align || "center"}
                            rowSpan={locationObj?.rowSpan || 1}
                            colSpan={1}
                            sx={({ typography: { fontWeightBold } }) => ({
                              fontSize: compactBodyFontSize,
                              fontWeight: fontWeightBold,
                              border: `1px solid ${cellBorderColor}`,
                              backgroundColor:
                                locationObj?.locationCompletion?.bgColor || "transparent",
                              color: Colors.PRIMARY,
                              padding: bodyPadding,
                              height: "auto",
                              minHeight: rowHeight,
                            })}
                          >
                            {locationObj?.locationCompletion?.value || ""}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return locationObj?.assets?.map((asset, assetIndex) => (
                      <TableRow key={asset?.key}>
                        {assetIndex === 0 && (
                          <TableCell
                            key={locationObj?.key}
                            colSpan={locationObj?.colSpan || 1}
                            align={locationObj?.align || "left"}
                            rowSpan={locationObj?.rowSpan || 1}
                            width={locationObj?.width || "auto"}
                            sx={({ typography: { fontWeightBold } }) => ({
                              fontSize: compactBodyFontSize,
                              fontWeight: fontWeightBold,
                              color: Colors.PRIMARY,
                              backgroundColor: "transparent",
                              border: `1px solid ${cellBorderColor}`,
                              borderLeft: "none",
                              padding: bodyPadding,
                              height: "auto",
                              minHeight: rowHeight,
                            })}
                          >
                            {locationObj.location}
                          </TableCell>
                        )}
                        <TableCell
                          align={asset?.align || "left"}
                          width={asset?.width || "auto"}
                          colSpan={asset?.colSpan || 1}
                          rowSpan={asset?.rowSpan || 1}
                          sx={({ typography: { fontWeightBold } }) => ({
                            fontSize: compactBodyFontSize,
                            fontWeight: fontWeightBold,
                            border: `1px solid ${cellBorderColor}`,
                            backgroundColor: "transparent",
                            color: Colors.PRIMARY,
                            padding: bodyPadding,
                            height: "auto",
                            minHeight: rowHeight,
                          })}
                        >
                          {asset?.title}
                        </TableCell>
                        {locationObj.reports.map((report) => {
                          if (report.type === "location") {
                            return (
                              assetIndex === 0 && (
                                <TableCell
                                  key={report?.key}
                                  align={report?.align || "left"}
                                  rowSpan={report?.rowSpan || 1}
                                  colSpan={report?.colSpan || 1}
                                  width={report?.width || "auto"}
                                  sx={({ typography: { fontWeightBold } }) => ({
                                    fontSize: compactBodyFontSize,
                                    fontWeight: fontWeightBold,
                                    border: `1px solid ${cellBorderColor}`,
                                    backgroundColor:
                                      report?.reportList[0]?.bgColor || "transparent",
                                    color: Colors.PRIMARY,
                                    padding: bodyPadding,
                                    height: "auto",
                                    minHeight: rowHeight,
                                  })}
                                >
                                  {report.reportList[0].completion}
                                </TableCell>
                              )
                            );
                          }
                          if (report.type === "asset_per_location") {
                            return (
                              <TableCell
                                align={report?.align || "left"}
                                rowSpan={report?.rowSpan || 1}
                                colSpan={report?.colSpan || 1}
                                width={report?.width || "auto"}
                                sx={({ typography: { fontWeightBold } }) => ({
                                  fontSize: compactBodyFontSize,
                                  fontWeight: fontWeightBold,
                                  border: `1px solid ${cellBorderColor}`,
                                  backgroundColor:
                                    report?.reportList[assetIndex]?.bgColor || "transparent",
                                  color: Colors.PRIMARY,
                                  padding: bodyPadding,
                                  height: "auto",
                                  minHeight: rowHeight,
                                })}
                              >
                                {report.reportList[assetIndex].completion}
                              </TableCell>
                            );
                          }
                          if (report.type === "multiple_assets") {
                            return (
                              <TableCell
                                align={report?.align || "left"}
                                rowSpan={report?.rowSpan || 1}
                                colSpan={report?.colSpan || 1}
                                width={report?.width || "auto"}
                                sx={({ typography: { fontWeightBold } }) => ({
                                  fontSize: compactBodyFontSize,
                                  fontWeight: fontWeightBold,
                                  border: `1px solid ${cellBorderColor}`,
                                  backgroundColor:
                                    report?.reportList[assetIndex]?.bgColor || "transparent",
                                  color: Colors.PRIMARY,
                                  padding: bodyPadding,
                                  height: "auto",
                                  minHeight: rowHeight,
                                })}
                              >
                                {report.reportList[assetIndex].completion}
                              </TableCell>
                            );
                          }

                          return null;
                        })}
                        {assetIndex === 0 && (
                          <TableCell
                            align={locationObj?.locationCompletion?.align || "center"}
                            rowSpan={locationObj?.rowSpan || 1}
                            colSpan={1}
                            sx={({ typography: { fontWeightBold } }) => ({
                              fontSize: compactBodyFontSize,
                              fontWeight: fontWeightBold,
                              border: `1px solid ${cellBorderColor}`,
                              backgroundColor:
                                locationObj?.locationCompletion?.bgColor || "transparent",
                              color: Colors.PRIMARY,
                              padding: bodyPadding,
                              height: "auto",
                              minHeight: rowHeight,
                            })}
                          >
                            {locationObj?.locationCompletion?.value || ""}
                          </TableCell>
                        )}
                      </TableRow>
                    ));
                  });
                  return rowData;
                }

                case "noData":
                  return (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <MDTypography variant="h4" color="secondary">
                          {Constants.NO_DATA_FOUND}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  );

                case Constants.REJECTED:
                default:
                  return (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        <MDTypography variant="h4" color="secondary">
                          {Constants.SOMETHING_WENT_WRONG}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  );
              }
            })()}
          </TableBody>
          <MDBox component="tfoot">
            {footerListArray.length > 0 &&
              footerListArray?.map((footerColumn, footerIdx) => (
                <TableRow key={footerColumn}>
                  {footerColumn?.map((column, nestFooterIdx) => (
                    <TableCell
                      key={column.key}
                      align={column.align ? column.align : "left"}
                      colSpan={column.colSpan ? column.colSpan : 1}
                      sx={({ typography: { fontWeightBold } }) => ({
                        fontSize: compactBodyFontSize,
                        fontWeight: fontWeightBold,
                        backgroundColor:
                          footerIdx === 0 && nestFooterIdx === 0
                            ? Colors.PRIMARY
                            : handleCellBackground(column.Footer),

                        color:
                          footerIdx === 0 && nestFooterIdx === 0 ? Colors.WHITE : Colors.PRIMARY,
                        width: column.width ? column.width : "auto",
                        border: column.Footer !== "" ? `1px solid ${cellBorderColor}` : "none",
                        borderTop: `1px solid ${cellBorderColor}`,
                        borderLeft: footerIdx === 0 && nestFooterIdx === 0 && "none",
                        borderBottom: footerIdx === footerListArray.length - 1 && "none",
                        borderRight: nestFooterIdx === footerColumn.length - 1 && "none",
                      })}
                    >
                      <MDBox
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <MDTypography
                          sx={{
                            fontSize,
                            fontWeight: "bold",
                            textTransform: "capitalize",
                            textAlign: "center",
                            color:
                              footerIdx === 0 && nestFooterIdx === 0
                                ? Colors.WHITE
                                : Colors.PRIMARY,
                          }}
                        >
                          {column.Footer}
                        </MDTypography>
                      </MDBox>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </MDBox>
        </Table>
      </TableContainer>
      {isDrawerVariant && (
        <MDBox sx={{ mt: 1.5, px: pxToRem(12), pb: pxToRem(4) }}>
          <MDBox
            sx={{
              height: pxToRem(4),
              borderRadius: pxToRem(4),
              backgroundColor: Colors.LIGHT_GRAY4,
              overflow: "hidden",
            }}
          >
            <MDBox
              sx={{
                height: "100%",
                width: `${0}%`,
                backgroundColor: Colors.PRIMARY,
                transition: "width 0.2s ease",
                borderRadius: pxToRem(4),
              }}
            />
          </MDBox>
        </MDBox>
      )}
      {extraContent}
    </MDBox>
  );
}

// Setting default values for the props of ReportCalculationTable
ReportCalculationTable.defaultProps = {
  isSorted: true,
  noEndBorder: false,
  loading: Constants.PENDING,
  licenseRequired: false,
  backgroundColor: "",
  textColor: "",
  extraContent: null,
  extraHeaders: [],
  footerList: [],
  containerHeight: null,
  variant: "default",
};

// Typechecking props for the ReportCalculationTable
ReportCalculationTable.propTypes = {
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  loading: PropTypes.string,
  licenseRequired: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  extraContent: PropTypes.node,
  extraHeaders: PropTypes.arrayOf(PropTypes.array),
  footerList: PropTypes.arrayOf(PropTypes.array),
  containerHeight: PropTypes.string,
  variant: PropTypes.oneOf(["default", "drawer"]),
};

export default ReportCalculationTable;
